import { CXoneVoiceClient } from '@nice-devone/voice-sdk';
import { ACDSessionManager } from '@nice-devone/core-sdk';
import { logger } from '../../../utils/logger';
import { CXoneEventsSubscriptionContext, EventSubscription } from '../subscription-context';
import { getWebRtcReadyState, storePendingAgentLeg } from '../webrtc-connection';
import {
  resolveAgentLegFinalState,
  resolveAgentLegId,
  resolveAgentLegStatus,
} from './nice-event-parsing.util';

export function subscribeAgentLegEvents(
  voiceClient: CXoneVoiceClient,
  acdSession: ACDSessionManager,
  ctx: CXoneEventsSubscriptionContext
): EventSubscription | null {
  if (!acdSession.agentLegSubject) {
    return null;
  }

  return acdSession.agentLegSubject.subscribe((event: unknown) => {
    logger.debug('[Events] Agent leg event received:', {
      event,
      voicePreferenceType: ctx.voicePreference?.type,
      isWebRtcConnected: ctx.isWebRtcConnectedRef.current,
    });

    if (!event || typeof event !== 'object') {
      return;
    }

    const legEvent = event as Record<string, unknown>;
    const legId = resolveAgentLegId(legEvent);
    const legStatus = resolveAgentLegStatus(legEvent);
    const legFinalState = resolveAgentLegFinalState(legEvent);

    logger.debug('[Events] Agent leg parsed:', { legId, legStatus, legFinalState });

    if (!legId) {
      return;
    }

    ctx.setAgentLegId(legId);
    ctx.setAgentLegStatus(legStatus);

    const normalizedLegStatus = legStatus.toLowerCase();
    const isActive =
      normalizedLegStatus === 'active' ||
      normalizedLegStatus === 'holding' ||
      normalizedLegStatus === 'muted' ||
      normalizedLegStatus === 'connected' ||
      normalizedLegStatus === 'joined';
    if (isActive) {
      ctx.setIsAgentLegActive(true);
    } else if (legFinalState) {
      ctx.setIsAgentLegActive(false);
      ctx.setAgentLegStatus(null);
    }

    const currentCall = ctx.callStateRef.current;
    if (
      normalizedLegStatus === 'dialing' &&
      (currentCall.status === 'idle' || currentCall.status === 'connecting') &&
      !currentCall.contactId
    ) {
      ctx.setCallState((prev) => ({
        ...prev,
        status: 'connecting',
      }));
    }

    if (ctx.voicePreference?.type !== 'softphone') {
      logger.warn('[Events] NOT in softphone mode - WebRTC handling skipped!', {
        voicePreferenceType: ctx.voicePreference?.type,
      });
      return;
    }

    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      voiceClient.handleAgentLegEvent(event as any);

      if (legStatus !== 'Dialing' && normalizedLegStatus !== 'dialing') {
        return;
      }

      const { webRtcReady } = getWebRtcReadyState(voiceClient);

      if (webRtcReady) {
        logger.debug('[Events] WebRTC ready - calling connectAgentLeg for:', legId);
        try {
          voiceClient.connectAgentLeg(legId);
        } catch (connectError) {
          logger.warn('[Events] connectAgentLeg failed:', connectError);
        }
      } else {
        storePendingAgentLeg(ctx.pendingAgentLegRef, legId);
      }
    } catch (error) {
      logger.error('[Events] FAILED to handle agent leg event:', error);
    }
  });
}

export function subscribeMuteEvents(
  acdSession: ACDSessionManager,
  ctx: CXoneEventsSubscriptionContext
): EventSubscription | null {
  if (!acdSession.muteEventSubject) {
    return null;
  }

  return acdSession.muteEventSubject.subscribe((event: unknown) => {
    if (event && typeof event === 'object') {
      const muteEvent = event as { isMuted?: boolean; muted?: boolean };
      const muted = muteEvent.isMuted ?? muteEvent.muted ?? false;
      ctx.setIsMuted(muted);
    }
  });
}
